'use client';
import { useState, useEffect } from 'react';
import { Modal } from '@/components/shared/modal';
import { Input } from '@/components/shared/input';
import { Textarea } from '@/components/shared/textarea';
import { Select } from '@/components/shared/select';
import { ImageInput } from '@/components/shared/image-input';
import { Button } from '@/components/shared/button';
import { submitTicket, getCurrentUserEmail } from './actions';
import { toast } from '@/components/shared/toast';
import { useTranslation } from '@/contexts/language-context';

export function ReportIssueModal({ onClose }) {
  const { t } = useTranslation();

  const urgencyOptions = [
    {
      value: 'Urgent',
      label: t('applications.reportIssue.urgency.urgent', 'Urgent')
    },
    {
      value: 'High',
      label: t('applications.reportIssue.urgency.high', 'High')
    },
    {
      value: 'Medium',
      label: t('applications.reportIssue.urgency.medium', 'Medium')
    },
    { value: 'Low', label: t('applications.reportIssue.urgency.low', 'Low') }
  ];

  const urgencyDescriptions = {
    Urgent: t(
      'applications.reportIssue.urgencyDescriptions.urgent',
      'Issues with functionality causing you to be unable to proceed (e.g., Client portal is unavailable or error messages)'
    ),
    High: t(
      'applications.reportIssue.urgencyDescriptions.high',
      'Issues causing incorrect or incomplete information to be populated (e.g., Address not found)'
    ),
    Medium: t(
      'applications.reportIssue.urgencyDescriptions.medium',
      "Issues with received loan offers, providers' information, or other fields (e.g., Incorrect location for provider)"
    ),
    Low: t(
      'applications.reportIssue.urgencyDescriptions.low',
      "Feature requests or minor bugs that do not affect the applications' functions (e.g., typos, incorrect translation, etc.)"
    )
  };
  const [formData, setFormData] = useState({
    description: '',
    urgency: '',
    contactName: '',
    contactEmail: '',
    screenshot: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  // Fetch current user's email when modal opens
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getCurrentUserEmail();
        if (userData && !userData.error) {
          setFormData((prev) => ({
            ...prev,
            contactEmail: userData.email,
            contactName: userData.name || ''
          }));
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    fetchUserData();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.description.trim()) {
      newErrors.description = t(
        'applications.reportIssue.validation.description',
        'Please describe the issue you are having'
      );
    }

    if (!formData.urgency) {
      newErrors.urgency = t(
        'applications.reportIssue.validation.urgency',
        'Please select the urgency of the issue'
      );
    }

    if (!formData.contactName.trim()) {
      newErrors.contactName = t(
        'applications.reportIssue.validation.contactName',
        'Please enter your contact name'
      );
    }

    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = t(
        'applications.reportIssue.validation.contactEmail',
        'Please enter your contact email'
      );
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
      newErrors.contactEmail = t(
        'applications.reportIssue.validation.contactEmailInvalid',
        'Please enter a valid email address'
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitTicket(formData);

      if (result.error) {
        toast.error(
          result.message ||
            t(
              'applications.reportIssue.messages.submitError',
              'Failed to submit ticket. Please try again.'
            )
        );
      } else {
        toast.success(
          t(
            'applications.reportIssue.messages.submitSuccess',
            'Ticket submitted successfully! We will get back to you soon.'
          )
        );
        onClose();
      }
    } catch (error) {
      console.error('Error submitting ticket:', error);
      toast.error(
        t(
          'applications.reportIssue.messages.unexpectedError',
          'An unexpected error occurred. Please try again.'
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const isFormValid = () => {
    return (
      formData.description.trim() &&
      formData.urgency &&
      formData.contactName.trim() &&
      formData.contactEmail.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)
    );
  };

  return (
    <Modal
      title={t('applications.reportIssue.title', 'Report Issue')}
      modalBoxClassName="max-w-xl max-h-[90vh] overflow-y-auto"
      closeButton={true}
      closeBackdrop={true}
      showButton={false}
      open={true}
      onClose={onClose}
      render={({ portal }) => (
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="flex items-center gap-2 mb-4">
            <p className="text-sm text-gray-600">
              {t(
                'applications.reportIssue.helpText',
                'Help us improve by reporting any issues you encounter'
              )}
            </p>
          </div>

          <Textarea
            label={t(
              'applications.reportIssue.labels.description',
              'Describe the issue you are having'
            )}
            placeholder={t(
              'applications.reportIssue.placeholders.description',
              'Please provide a detailed description of the issue...'
            )}
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            error={errors.description}
            rows={2}
            required
          />

          <Select
            label={t(
              'applications.reportIssue.labels.urgency',
              'Urgency of the issue'
            )}
            placeholder={t(
              'applications.reportIssue.placeholders.urgency',
              'Select urgency level'
            )}
            options={urgencyOptions}
            value={urgencyOptions.find(
              (option) => option.value === formData.urgency
            )}
            onChange={(value) => handleInputChange('urgency', value || '')}
            menuPortalTarget={portal}
            error={errors.urgency}
            required
          />

          {formData.urgency && (
            <div
              className={`p-3 rounded-lg ${
                formData.urgency === 'Urgent'
                  ? 'bg-red-50'
                  : formData.urgency === 'High'
                    ? 'bg-orange-50'
                    : formData.urgency === 'Medium'
                      ? 'bg-yellow-50'
                      : 'bg-blue-50'
              }`}
            >
              <p
                className={`text-sm ${
                  formData.urgency === 'Urgent'
                    ? 'text-red-800'
                    : formData.urgency === 'High'
                      ? 'text-orange-800'
                      : formData.urgency === 'Medium'
                        ? 'text-yellow-800'
                        : 'text-blue-800'
                }`}
              >
                <strong>{formData.urgency}:</strong>{' '}
                {urgencyDescriptions[formData.urgency]}
              </p>
            </div>
          )}

          <Input
            label={t(
              'applications.reportIssue.labels.contactName',
              'Contact Name'
            )}
            placeholder={
              isLoadingUser
                ? t(
                    'applications.reportIssue.placeholders.loading',
                    'Loading...'
                  )
                : t(
                    'applications.reportIssue.placeholders.contactName',
                    'Enter your full name'
                  )
            }
            value={formData.contactName}
            onChange={(e) => handleInputChange('contactName', e.target.value)}
            error={errors.contactName}
            disabled={isLoadingUser}
            required
          />

          <Input
            label={t(
              'applications.reportIssue.labels.contactEmail',
              'Contact Email'
            )}
            type="email"
            placeholder={
              isLoadingUser
                ? t(
                    'applications.reportIssue.placeholders.loading',
                    'Loading...'
                  )
                : t(
                    'applications.reportIssue.placeholders.contactEmail',
                    'Enter your email address'
                  )
            }
            value={formData.contactEmail}
            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
            error={errors.contactEmail}
            disabled={isLoadingUser}
            required
          />

          <ImageInput
            label={t(
              'applications.reportIssue.labels.screenshot',
              'Screenshot (Optional)'
            )}
            placeholder={t(
              'applications.reportIssue.placeholders.screenshot',
              'Upload a screenshot to help us understand the issue'
            )}
            value={formData.screenshot}
            onChange={(value) => handleInputChange('screenshot', value)}
            useBase64={true}
            accept="image/png,image/jpeg,image/webp,image/gif"
          />

          <div className="flex pt-4">
            <Button
              type="submit"
              disabled={isSubmitting || !isFormValid()}
              className="flex-1 btn-neutral"
            >
              {isSubmitting
                ? t(
                    'applications.reportIssue.buttons.submitting',
                    'Submitting...'
                  )
                : t('applications.reportIssue.buttons.submit', 'Submit Ticket')}
            </Button>
          </div>
        </form>
      )}
    />
  );
}
